import React, { memo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import images from 'Assets/Images'
import { MessageErrorInput } from 'Components/Message/Error/input'
import { useFormChildLogic } from '../index'
import classNames from 'classnames'
import Info from '../../../Assets/Images/icons/info'
import Tooltip from '../../Tooltip/Tooltip'

export const InputUnstyled = memo(props => {
  const {
    id,
    placeholder,
    disabled = false,
    name,
    meta = {},
    input = {},
    validator,
    min,
    onKeyDown = () => {},
    useExternalValue,
    inputClassName,
    hint,
    maxLength,
    inputRef,
  } = props
  const [showHint, setShowHint] = useState(false)
  // Some old components are still using "value" prop
  const initValue = props.initValue || props.value

  const { updateForm, value, error } = useFormChildLogic({
    name,
    validator,
    initValue,
  })

  var onChange = props.onChange || input.onChange
  var onBlur = props.onBlur || input.onBlur
  const defaultValue =
    props.defaultValue !== undefined ? props.defaultValue : input.defaultValue

  const type = props.type || 'text'
  const className = `${
    props.className
  } ${type}-input-wrapper ${props.className || ''} ${
    hasError() ? 'has-error' : ''
  }`

  const handleChange = useCallback(
    e => {
      onChange && onChange(e)
      updateForm(e.target.value)
    },
    [validator, onChange],
  )

  const handleWheel = useCallback(
    e => {
      if (type === 'number') {
        e.target.blur()
      }
    },
    [type],
  )

  return [
    <div
      key="input"
      className={classNames({
        [inputClassName]: inputClassName,
        [className]: !inputClassName,
      })}
    >
      {renderLabel(props.cypress_id)}
      <input
        onWheel={handleWheel}
        id={id}
        test-id={props['test-id']}
        name={name}
        disabled={disabled}
        defaultValue={defaultValue === undefined ? '' : defaultValue}
        placeholder={placeholder}
        type={type}
        min={min}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        value={useExternalValue ? props.value : value || input.value}
        className={classNames({
          [inputClassName]: inputClassName,
          'errorStyles': hasError(),
        })}
        cypress_id={props.cypress_id}
        maxLength={maxLength}
        ref={inputRef}
      />
      {renderIcon()}
      <label>{renderError(error)}</label>
    </div>,
  ]

  /**
   * Render label
   * @returns JSX.Element | null
   */
  function renderLabel(cypress_id) {
    const { label, id, content } = props

    return label ? (
      <div className="labelContainer">
        <div className={'labelBox'}>
          <label
            className="label"
            htmlFor={id}
            cypress_id={cypress_id ? `${cypress_id}-label` : ''}
          >
            {label}
          </label>
          {content && content !== '' && <Tooltip content={content} />}
          {hint ? (
            <div>
              <div
                className={'hint'}
                style={showHint ? { display: 'block' } : { display: 'none' }}
              >
                {hint}
              </div>
              <div
                className={'infoIcon'}
                onMouseEnter={() => setShowHint(true)}
                onMouseLeave={() => setTimeout(() => setShowHint(false), 1000)}
              >
                <Info />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    ) : null
  }

  /**
   * Render icon
   * @returns JSX.Element | null
   */
  function renderIcon() {
    const icon = props.icon
    let iconImage

    if (icon) {
      switch (icon) {
        case 'user':
          iconImage = images.userIcon
          break
        case 'password':
          iconImage = images.passwordIcon
          break
        case 'email':
          iconImage = images.emailIcon
          break
      }
    }

    return icon ? <img className="icon" src={iconImage} alt="icon" /> : null
  }

  /**
   * Input has error
   * @returns boolean
   */
  function hasError(error) {
    return props.error || (meta.touched && meta.error) || error
  }

  /**
   * Render field error
   * @returns JSX.Element | null
   */
  function renderError(error) {
    const inputError = meta.error || props.error || error

    return hasError(error) ? (
      <MessageErrorInput key="errorMessage" message={inputError} />
    ) : null
  }
})

InputUnstyled.propTypes = {
  name: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string,
  icon: PropTypes.oneOf(['user', 'password', 'email']),
  placeholder: PropTypes.string,
  type: PropTypes.string,
  input: PropTypes.shape({
    onChange: PropTypes.func,
  }),
  inputRef: PropTypes.oneOfType([
    PropTypes.func, // For callback refs
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }), // For object refs
    PropTypes.oneOf([null]), // To explicitly allow null
  ]),
}
